import { SetAssetPracticeTask } from "./SetAssetPracticeTask.js";
import { LibraryDocumentPicker } from "./LibraryDocumentPicker.js";
import type { LibraryItem } from "./fileLibrary.js";
import { ApiError, send } from "./api.js";
import "./pages/files.css";
import { useEffect, useRef, useState } from "react";
import {
  describeAttachmentKind,
  formatAttachmentSize,
  LESSON_ATTACHMENT_ACCEPT,
  listLessonNoteAttachments,
  MAX_LESSON_ATTACHMENT_MB,
  removeLessonNoteAttachment,
  uploadLessonNoteAttachment,
  setLessonNoteAttachmentSharing,
  type LessonNoteAttachment,
  type LessonNoteAttachmentRemoval,
} from "./lessonNoteAttachmentApi.js";

/**
 * The files on the lesson being written up, and how to add one.
 *
 * The same component on both write-up pages. A private lesson and a School
 * one differ only in which note the file hangs off, and that is the
 * `basePrefix` this is handed; everything a tutor does here — pick, see,
 * open, remove — is the same job in both.
 *
 * A note that has not been saved yet has nowhere to put a file, so choosing
 * one saves the draft first. That is exactly what the groove builder beside
 * it already does, and it is the difference between "attach this" and "save
 * a draft, then attach this", which is not a decision worth asking anybody to
 * make.
 */
export function LessonNoteAttachments({
  noteId,
  basePrefix,
  storageConfigured,
  ensureNote,
  onNoteCreated,
  onRemoved,
  onBusyChange,
  onAttentionChange,
  headingId,
  disabled = false,
  refreshVersion = 0,
  /** What the family will be told this belongs to, for the helper line. */
  audience = "the family",
  libraryFor,
  practiceTarget,
  onPracticeChanged,
}: {
  noteId: string | null;
  /**
   * Everything before the note id. Both domains address a note's files as
   * `<prefix>/<note id>/attachments`, so this is the whole of the difference
   * between them.
   */
  basePrefix: string;
  /**
   * Whether the portal has object storage, as the page that owns this note
   * was told when it loaded.
   *
   * Required, and deliberately not defaulted: a note that has not been saved
   * has no file list to ask, so without this the panel would have to assume
   * storage works and would offer a picker that creates a draft and only then
   * discovers it cannot upload. The page knows; it has to say.
   */
  storageConfigured: boolean;
  ensureNote: () => Promise<string | null>;
  onNoteCreated?: (noteId: string) => void;
  onRemoved?: (result: LessonNoteAttachmentRemoval) => void;
  onBusyChange?: (busy: boolean) => void;
  onAttentionChange?: (needsAttention: boolean) => void;
  headingId: string;
  disabled?: boolean;
  refreshVersion?: number;
  audience?: string;
  /** `student:<id>` or `pupil:<id>` identifies the destination for reuse eligibility. */
  libraryFor?: string;
  practiceTarget?: import("@wmf/shared/assignedAssetPractice").AssetPracticeTarget;
  onPracticeChanged?: () => void;
}) {
  const baseFor = (id: string) => `${basePrefix}/${encodeURIComponent(id)}/attachments`;

  /**
   * The note this panel is working against.
   *
   * Attaching a file to an unsaved write-up creates the note, and this panel
   * must not then wait for the page above to hand the new id back down before
   * it can show what it just uploaded. It tells the page — `onNoteCreated` —
   * and gets on with it.
   */
  const [createdNoteId, setCreatedNoteId] = useState<string | null>(null);
  const activeNoteId = noteId ?? createdNoteId;
  const activeContext = useRef({ noteId: activeNoteId, basePrefix, libraryFor });
  activeContext.current = { noteId: activeNoteId, basePrefix, libraryFor };
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const [attachments, setAttachments] = useState<LessonNoteAttachment[]>([]);
  /**
   * What the file list said, once it has been read. Until then — and always,
   * for a note that does not exist yet — the page's own answer stands.
   */
  const [listedStorage, setListedStorage] = useState<boolean | null>(null);
  const storageAvailable = listedStorage ?? storageConfigured;
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const listVersion = useRef(0);
  useEffect(() => { onBusyChange?.(busy); }, [busy, onBusyChange]);
  useEffect(() => { onAttentionChange?.(problem !== null); }, [problem, onAttentionChange]);

  // Once the page has caught up, its id is the only one in play again.
  useEffect(() => {
    if (noteId !== null) setCreatedNoteId(null);
  }, [noteId]);

  useEffect(() => {
    if (!activeNoteId) {
      setAttachments([]);
      setListedStorage(null);
      return undefined;
    }
    let current = true;
    const version = ++listVersion.current;
    setLoading(true);
    setProblem(null);
    listLessonNoteAttachments(baseFor(activeNoteId))
      .then((result) => {
        if (!current || version !== listVersion.current) return;
        setAttachments(result.attachments);
        setListedStorage(result.storageConfigured);
      })
      .catch((cause: unknown) => {
        if (current && version === listVersion.current) {
          setProblem(cause instanceof Error ? cause.message : "Could not load the files.");
        }
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [activeNoteId, basePrefix, refreshVersion]);

  const mutationLock = useRef(false);

  async function attach(file: File | LibraryItem): Promise<boolean> {
    // The button is already disabled without storage. This is the second lock
    // on the same door, because the thing on the other side of it is creating
    // a draft note for an upload that cannot succeed.
    if (!storageAvailable || disabled || mutationLock.current || loading) return false;
    mutationLock.current = true;
    let target = activeNoteId;
    const isCurrent = () => mounted.current && activeContext.current.basePrefix === basePrefix
      && activeContext.current.libraryFor === libraryFor
      && (activeContext.current.noteId === activeNoteId || activeContext.current.noteId === target);

    setBusy(true);
    onBusyChange?.(true);
    setProblem(null);
    try {
      // A brand-new write-up has no note yet, so make one before there is
      // anywhere to put this.
      target = activeNoteId ?? (await ensureNote());
      if (!isCurrent()) return false;
      if (!target) {
        setProblem("The lesson note could not be saved, so the file was not attached.");
        return false;
      }

      const attachment = file instanceof File
        ? await uploadLessonNoteAttachment(baseFor(target), file)
        : (await send<{ attachment: LessonNoteAttachment }>("POST", `${baseFor(target)}/library`, { source: file.source, id: file.id })).attachment;
      if (!isCurrent()) return false;
      listVersion.current++;
      setLoading(false);
      if (target === activeNoteId) {
        setAttachments((current) => current.some(item => item.id === attachment.id)
          ? current.map(item => item.id === attachment.id ? attachment : item)
          : [...current, attachment]);
        return true;
      }
      // The first file on a write-up that had not been saved yet. Adopt the
      // note the save just made — after the upload, so the reload that
      // follows cannot race ahead of the file it is about to look for.
      setAttachments([attachment]);
      setCreatedNoteId(target);
      onNoteCreated?.(target);
      return true;
    } catch (cause: unknown) {
      if (isCurrent()) setProblem(cause instanceof Error ? cause.message : "That file could not be attached.");
      return false;
    } finally {
      mutationLock.current = false;
      if (isCurrent()) { setBusy(false); onBusyChange?.(false); }
    }
  }

  async function remove(attachment: LessonNoteAttachment) {
    if (!activeNoteId || disabled || mutationLock.current || loading) return;
    mutationLock.current = true;
    const target = { noteId: activeNoteId, basePrefix };
    const isCurrent = () => mounted.current && activeContext.current.noteId === target.noteId && activeContext.current.basePrefix === target.basePrefix;
    setBusy(true);
    onBusyChange?.(true);
    setProblem(null);
    try {
      const result = await removeLessonNoteAttachment(baseFor(activeNoteId), attachment.id);
      if (!isCurrent()) return;
      listVersion.current++;
      setAttachments((current) => current.filter((item) => item.id !== attachment.id));
      onRemoved?.(result);
    } catch (cause: unknown) {
      if (isCurrent()) setProblem(cause instanceof Error ? cause.message : "That file could not be removed.");
    } finally {
      mutationLock.current = false;
      if (isCurrent()) { setBusy(false); onBusyChange?.(false); }
    }
  }

  async function changeSharing(attachment: LessonNoteAttachment) {
    if (!activeNoteId || disabled || mutationLock.current || loading) return;
    mutationLock.current = true;
    const target = { noteId: activeNoteId, basePrefix };
    const isCurrent = () => mounted.current && activeContext.current.noteId === target.noteId && activeContext.current.basePrefix === target.basePrefix;
    setBusy(true); onBusyChange?.(true); setProblem(null);
    try {
      const result = await setLessonNoteAttachmentSharing(baseFor(target.noteId), attachment.id, attachment.shareable === false);
      if (!isCurrent()) return;
      listVersion.current++;
      setAttachments(current => current.map(item => item.id === result.id ? { ...item, shareable: result.shareable } : item));
    } catch (cause) {
      if (isCurrent()) setProblem(cause instanceof ApiError && cause.status === 404
        ? "Your access to change this asset is no longer available. Reload the lesson to check your teaching access."
        : cause instanceof Error ? cause.message : "Could not change asset sharing.");
    } finally {
      mutationLock.current = false;
      if (isCurrent()) { setBusy(false); onBusyChange?.(false); }
    }
  }

  return (
    <section className="lesson-notes-files" aria-labelledby={headingId}>
      <div className="lesson-notes-files-head">
        <div>
          <h2 id={headingId}>Files for this lesson</h2>
          <p className="small muted">
            The music worked from, a photograph of the page, or a recording of how it should sound.
            Files go out with the note when it is published to {audience}.
            {" "}Shareable assets can be reused by tutors. Private assets can only be reused for this student.
            {" "}Existing assignments keep access. Hub policy: files and teaching assets must not contain personal information.
          </p>
        </div>
        <button
          type="button"
          className="quiet"
          disabled={disabled || busy || loading || !storageAvailable}
          onClick={() => picker.current?.click()}
        >
          {busy ? "Working…" : "+ Attach a file"}
        </button>
      </div>

      <LibraryDocumentPicker disabled={disabled || busy || loading || !storageAvailable} onSelect={attach} scope={libraryFor} />

      <input
        ref={picker}
        type="file"
        accept={LESSON_ATTACHMENT_ACCEPT}
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Cleared straight away, so choosing the same file again after a
          // failure still counts as a change.
          event.target.value = "";
          if (file) void attach(file);
        }}
      />

      {problem && (
        <div className="error small" role="alert">
          {problem}
        </div>
      )}

      {!storageAvailable && (
        <div className="notice small" role="status">
          File storage is not configured for this portal yet, so files cannot be attached. The
          written note and its grooves are unaffected.
        </div>
      )}

      <div className="panel lesson-notes-files-panel">
        {loading && attachments.length === 0 ? (
          <p className="empty">Loading files…</p>
        ) : attachments.length === 0 ? (
          <p className="small muted lesson-notes-files-empty">
            {activeNoteId
              ? "No files yet. PDFs, images, audio and video up to " +
                `${MAX_LESSON_ATTACHMENT_MB} MB.`
              : storageAvailable
                ? "You can attach a file now. The lesson draft will be saved automatically when one needs somewhere to go."
                : "No files attached."}
          </p>
        ) : activeNoteId === null ? null : (
          <ul className="lesson-notes-file-list">
            {attachments.map((attachment) => {
              const size = formatAttachmentSize(attachment.fileSize);
              return (
                <li className="lesson-notes-file" key={attachment.id}>
                  <a
                    href={`/api${baseFor(activeNoteId)}/${encodeURIComponent(attachment.id)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="lesson-notes-file-name"
                  >
                    {attachment.fileName}
                  </a>
                  <span className="small muted lesson-notes-file-meta">
                    {describeAttachmentKind(attachment)}
                    {size ? ` · ${size}` : ""}
                    {attachment.shareable === false ? " · Private" : " · Shareable"}
                  </span>
                  <div className="lesson-notes-file-actions">
                    {practiceTarget && <SetAssetPracticeTask target={practiceTarget} source={{ kind: "lesson_file", noteId: activeNoteId, id: attachment.id }}
                      assetTitle={attachment.fileName} disabled={disabled || busy || loading} onSaved={onPracticeChanged}
                      onBusyChange={(value) => { mutationLock.current = value; setBusy(value); onBusyChange?.(value); }} />}
                    <button type="button" className="quiet small" disabled={disabled || busy || loading}
                      aria-label={`${attachment.shareable === false ? "Make shareable" : "Make private"}: ${attachment.fileName}`}
                      onClick={() => void changeSharing(attachment)}>
                      {attachment.shareable === false ? "Make shareable" : "Make private"}
                    </button>
                    <button
                      type="button"
                      className="link small"
                      disabled={disabled || busy}
                      aria-label={`Remove ${attachment.fileName}`}
                      onClick={() => void remove(attachment)}
                    >
                      remove
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
